import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
    filter,
    switchMap
} from 'rxjs/operators';

import {
    DatiqAppConfig,
    DatiqEnvironments,
    IDatiqAction,
    IDatiqRoute,
    IDatiqRulesData,
    IDatiqUnavItem,
} from '@dat/datiq-core/models';
import { DatiqAppStoreService } from '../store';
import { DatiqRulesEvaluatorService } from '@dat/datiq-rules';

@Injectable({
    providedIn: 'root',
})
export class DatiqAppStateService {

    constructor(
        private _store: DatiqAppStoreService,
        private _rule: DatiqRulesEvaluatorService
    ) {}

    isCurrentRouteUnlocked$(): Observable<boolean> {
        return this._store.isCurrentRouteUnlocked$();
    }

    lockCurrentRoute(): void {
        this._store.lockCurrentRoute();
    }

    unlockCurrentRoute(): void {
        this._store.unlockCurrentRoute();
    }

    loaded(config: DatiqAppConfig): void {
        this._store.loaded(config);
    }
    loading(): void {
        this._store.loading();
    }

    getLinkRules$(): Observable<IDatiqRulesData<IDatiqUnavItem>[]> {
        return this._store.getLinkRules$();
    }

    getValidLinks$(): Observable<IDatiqUnavItem[]> {
        return this.getLinkRules$().pipe(
            switchMap(
                (
                    unavRules: IDatiqRulesData<IDatiqUnavItem>[]
                ): Observable<IDatiqUnavItem[]> =>
                    this._rule.getPassedData$(unavRules) as Observable<
                        IDatiqUnavItem[]
                    >
            )
        );
    }

    getUserLinkRules$(): Observable<IDatiqRulesData<IDatiqUnavItem>[]> {
        return this._store.getUserLinkRules$();
    }

    getValidUserLinks$(): Observable<IDatiqUnavItem[]> {
        return this.getUserLinkRules$().pipe(
            switchMap(
                (
                    userLinkRules: IDatiqRulesData<IDatiqUnavItem>[]
                ): Observable<IDatiqUnavItem[]> => {
                    return this._rule.getPassedData$(
                        userLinkRules
                    ) as Observable<IDatiqUnavItem[]>;
                }
            )
        );
    }

    getAppLoaded$(): Observable<boolean> {
        return this._store.getAppLoaded$();
    }

    initializeApp(): void {
        this._store.initializeApp();
    }

    isEnvironment$(environment: DatiqEnvironments): Observable<boolean> {
        return this.getAppLoaded$().pipe(
            filter((loaded: boolean) => loaded),
            switchMap(() => this._store.isEnvironment$(environment))
        );
    }

    getEnvironment$(): Observable<string> {
        return this.getAppLoaded$().pipe(
            filter((loaded: boolean) => loaded),
            switchMap(() => this._store.getEnvironment$())
        );
    }

    /**
     * This function will take the settings for the action and will dispatch into the store.
     * The idea here is to use actions with effects in local stores to do the action rather than
     * trying to add listeners.
     * @param data - data for the action
     */
    fireAction(data: IDatiqAction): void {
        this._store.fireAction(data);
    }

    setRoute(route: IDatiqRoute): void {
        this._store.setRoute(route);
    }

    isCurrentRoute$(value: string): Observable<boolean> {
        return this._store.isCurrentRoute$(value);
    }

    containsCurrentRoute$(value: string): Observable<boolean> {
        return this._store.containsCurrentRoute$(value);
    }

    getCurrentRoute$(): Observable<IDatiqRoute> {
        return this._store.getCurrentRoute$();
    }
}
